import { useRef, useState, type ChangeEvent } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';

import { readBadge, type BadgeRead } from '../../api/intake';
import { prepareBadgePhoto } from './badgeImage';
import { CameraCapture, liveCameraAvailable } from './CameraCapture';

/** What step 1 produced: the (downscaled) photo, the QR text and what the
 *  badge reader found — any of which may be missing. */
export interface BadgeCaptureResult {
  /** data:image/jpeg;base64,… */
  photo: string;
  /** The QR payload — the ACS attendee ID. */
  qr: string | null;
  read: BadgeRead | null;
}

/** The conference ID to store: the QR wins, else the ID printed on the badge. */
export function badgeConferenceId(r: BadgeCaptureResult | null): string | null {
  return r?.qr || r?.read?.attendee_id || null;
}

const FOUND: Array<[keyof BadgeRead, string]> = [
  ['full_name', 'Name'],
  ['organization', 'Organisation'],
  ['city', 'City'],
  ['state_region', 'State / region'],
  ['country', 'Country'],
  ['badge_role', 'Badge'],
  ['conference_name', 'Conference'],
];

function friendlyReadError(err: unknown): string {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 503) return "Badge reading isn't set up — type the details in on the next step.";
  return "Couldn't read the badge — type the details in on the next step.";
}

/**
 * Step 1 of the booth flow (CONFERENCE-INTAKE): photograph the conference
 * badge. On a tablet/phone "Take badge photo" opens the rear camera; on a
 * laptop it's a file picker. The photo is downscaled, its QR decoded in the
 * browser, and the printed details read by the server's vision model — all
 * of it a pre-fill the operator confirms on the next step.
 */
export function BadgeCapture({
  tenantId,
  value,
  onChange,
  onSkip,
  read: readFn,
}: {
  tenantId: string;
  value: BadgeCaptureResult | null;
  onChange: (result: BadgeCaptureResult | null) => void;
  onSkip: () => void;
  /** How to read the badge — the participant's phone uses its join session
   *  (BOOTH-SELF-ENROL) instead of a staff login. */
  read?: (photo: string) => Promise<BadgeRead>;
}) {
  const cameraRef = useRef<HTMLInputElement | null>(null);
  const pickerRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState<'photo' | 'reading' | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  // A retake while the previous read is in flight must not let the old
  // response land on top of the new photo.
  const attempt = useRef(0);

  const [cameraOpen, setCameraOpen] = useState(false);

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    await processPhoto(file, null);
  };

  // Live camera where the browser allows it (laptops, https); on a phone or
  // tablet over plain http the camera input opens the device camera instead.
  const takePhoto = () => {
    if (liveCameraAvailable()) setCameraOpen(true);
    else cameraRef.current?.click();
  };

  /** `seenQr`: a QR already decoded from the live preview — kept when the
   *  still itself doesn't decode (motion blur on the last frame). */
  const processPhoto = async (file: Blob, seenQr: string | null) => {
    const mine = ++attempt.current;
    setError(null);
    setReadError(null);
    setBusy('photo');
    onChange(null);
    let photo: string;
    let qr: string | null;
    try {
      const prepared = await prepareBadgePhoto(file);
      photo = prepared.dataUrl;
      qr = prepared.qr ?? seenQr;
    } catch (err) {
      if (mine !== attempt.current) return;
      setBusy(null);
      setError(err instanceof Error ? err.message : "couldn't open that photo");
      return;
    }
    if (mine !== attempt.current) return;
    setPreview(photo);
    setBusy('reading');
    let read: BadgeRead | null = null;
    try {
      read = await (readFn ? readFn(photo) : readBadge(tenantId, photo));
    } catch (err) {
      if (mine !== attempt.current) return;
      setReadError(friendlyReadError(err));
    }
    if (mine !== attempt.current) return;
    setBusy(null);
    onChange({ photo, qr, read });
  };

  const shown = value?.photo ?? preview;
  const found = value?.read
    ? FOUND.filter(([k]) => value.read?.[k]).map(([k, label]) => [label, value.read?.[k] as string])
    : [];
  const confId = badgeConferenceId(value);

  return (
    <Stack spacing={3} alignItems="center" sx={{ py: 2 }}>
      <Typography variant="h5" component="h2" textAlign="center">
        Photograph the conference badge
      </Typography>
      <Typography color="text.secondary" textAlign="center" sx={{ maxWidth: 560 }}>
        Hold the badge flat, fill the frame, and make sure the QR code and the
        printed name are sharp. The details are filled in for you to check.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Button
          variant="contained"
          size="large"
          startIcon={<PhotoCameraIcon />}
          onClick={takePhoto}
          disabled={busy !== null}
          sx={{ minHeight: 64, px: 4, fontSize: '1.1rem' }}
        >
          {shown ? 'Retake badge photo' : 'Take badge photo'}
        </Button>
        <Button
          variant="outlined"
          size="large"
          startIcon={<PhotoLibraryIcon />}
          onClick={() => pickerRef.current?.click()}
          disabled={busy !== null}
          sx={{ minHeight: 64, px: 3 }}
        >
          Choose a photo
        </Button>
      </Stack>
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => void onFile(e)}
        data-testid="badge-camera-input"
      />
      <input
        ref={pickerRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => void onFile(e)}
        data-testid="badge-file-input"
      />

      <CameraCapture
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={(blob, seenQr) => {
          setCameraOpen(false);
          void processPhoto(blob, seenQr);
        }}
        onUseFile={() => {
          setCameraOpen(false);
          pickerRef.current?.click();
        }}
      />

      {busy && (
        <Stack direction="row" spacing={1.5} alignItems="center" role="status">
          <CircularProgress size={24} />
          <Typography>{busy === 'photo' ? 'Processing the photo…' : 'Reading the badge…'}</Typography>
        </Stack>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {readError && !busy && <Alert severity="warning">{readError}</Alert>}

      {shown && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start" sx={{ width: '100%', maxWidth: 820 }}>
          <Box
            component="img"
            src={shown}
            alt="Badge photo"
            sx={{
              width: { xs: '100%', md: 320 },
              maxHeight: 320,
              objectFit: 'contain',
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
              bgcolor: 'action.hover',
            }}
          />
          {value && !busy && (
            <Paper variant="outlined" sx={{ p: 2, flex: 1, width: '100%' }}>
              <Typography variant="subtitle2" gutterBottom>
                Found on the badge
              </Typography>
              {found.length === 0 && !confId ? (
                <Typography color="text.secondary">
                  Nothing could be read — you can type the details in next.
                </Typography>
              ) : (
                <Box
                  component="dl"
                  sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, rowGap: 0.5 }}
                >
                  {found.map(([label, v]) => (
                    <Box key={label} sx={{ display: 'contents' }}>
                      <Typography component="dt" color="text.secondary">
                        {label}
                      </Typography>
                      <Typography component="dd" sx={{ m: 0 }}>
                        {v}
                      </Typography>
                    </Box>
                  ))}
                  {confId && (
                    <Box sx={{ display: 'contents' }}>
                      <Typography component="dt" color="text.secondary">
                        Conference ID
                      </Typography>
                      <Typography component="dd" sx={{ m: 0, fontFamily: 'monospace' }}>
                        {confId}
                        {value.qr ? ' (QR)' : ''}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
          )}
        </Stack>
      )}

      <Link component="button" type="button" onClick={onSkip} disabled={busy !== null} sx={{ fontSize: '1rem' }}>
        Skip — no badge
      </Link>
    </Stack>
  );
}
